import * as _ from 'lodash';
import { Flex, Text, Icon, Button, Label, Divider, Box, Toggle } from '@vibe/core';
import { AppModal, AppModalContent } from '@gorilla/ui/src/components/AppModal';
import { SettingsKnobs } from '@vibe/icons';

type FieldGroupAdvancedSettingsModalProps = {
  show: boolean;
  board?: { type: string };
  fieldGroup: any;
  onChange: (updatedFieldGroup: any) => void;
  setShowBoardDiagnoseModal: (show: boolean) => void;
  onClose: () => void;
};

export default function FieldGroupAdvancedSettingsModal({
  show,
  onClose,
  fieldGroup,
  onChange,
  board,
  setShowBoardDiagnoseModal,
}: FieldGroupAdvancedSettingsModalProps) {
  return (
    <AppModal
      show={show}
      alertDialog={true}
      id="field-group-advanced-settings-modal"
      width="620px"
      onClose={() => {
        onClose();
      }}
    >
      <AppModalContent>
        <Flex direction="row" align="center" gap={4} style={{ marginBottom: '24px' }}>
          <Flex gap={8}>
            <Icon icon={SettingsKnobs} size={20} />
            <div style={{ maxWidth: '200px' }}>
              <Text type="text1" weight="medium" ellipsis>
                Advanced board settings
              </Text>
            </div>
          </Flex>
        </Flex>
        <Flex direction="row" align="start" gap={32}>
          <Flex gap={8} direction="column" align="start" style={{ width: '100%' }}>
            <div style={{ width: 'inherit' }}>
              {/* Column names */}
              <div style={{ marginBottom: '16px' }}>
                <Flex gap={12} direction="row" style={{ width: '100%' }}>
                  <Box>
                    <Toggle
                      areLabelsHidden
                      className="Toggle"
                      size="small"
                      isSelected={!!fieldGroup.show_headlines}
                      onChange={(value: boolean) => {
                        onChange({ ...fieldGroup, show_headlines: value });
                      }}
                    />
                  </Box>
                  <Text type="text2" weight="medium">
                    Enable column names
                  </Text>
                </Flex>
                <Text type="text3" color="secondary" ellipsis={false} style={{ marginTop: '8px' }}>
                  Embed the column names above board items in the exported spreadsheet. Enable on the very first board.
                </Text>
              </div>

              {/* Column names */}
              {board?.type === 'board' ? (
                <>
                  <Divider withoutMargin />
                  <div style={{ marginTop: '16px', marginBottom: '16px' }}>
                    <Flex gap={12} direction="row" style={{ width: '100%' }}>
                      <Box>
                        <Toggle
                          areLabelsHidden
                          className="Toggle"
                          size="small"
                          isSelected={!!fieldGroup.from_context}
                          onChange={(value: boolean) => {
                            onChange({ ...fieldGroup, from_context: value });
                          }}
                        />
                      </Box>
                      <Text type="text2" weight="medium">
                        Enable dynamic board
                      </Text>
                    </Flex>
                    <Text type="text3" color="secondary" ellipsis={false} style={{ marginTop: '8px' }}>
                      Use as dynamic board for fetching items based on the context where the export is triggered.{' '}
                      <a
                        href="https://getgorilla.app/products/spreadsheet-gorilla/dynamic-boards"
                        target="_blank"
                        rel="noreferrer"
                        style={{ fontSize: 'inherit', lineHeight: 'inherit' }}
                      >
                        Learn more
                      </a>
                    </Text>
                  </div>
                </>
              ) : null}

              <Divider withoutMargin />

              {/* Formulas */}
              <div style={{ marginTop: '16px' }}>
                <Flex gap={12} direction="row" style={{ width: '100%' }}>
                  <Box>
                    <Toggle
                      areLabelsHidden
                      className="Toggle"
                      size="small"
                      isSelected={fieldGroup.formula_resolution === 'from_api'}
                      onChange={(value: boolean) => {
                        onChange({ ...fieldGroup, formula_resolution: value ? 'from_api' : 'client_side' });
                      }}
                    />
                  </Box>
                  <Text type="text2" weight="medium">
                    Enable formulas via API
                  </Text>
                </Flex>
                <Text type="text3" color="secondary" ellipsis={false} style={{ marginTop: '8px' }}>
                  When enabled, formula columns will be resolved via the monday.com API and not in legacy mode as by default. Please read
                  about the advantages and disadvantages of both options.{' '}
                  <a
                    href="https://getgorilla.app/products/spreadsheet-gorilla/formulas"
                    target="_blank"
                    rel="noreferrer"
                    style={{ fontSize: 'inherit', lineHeight: 'inherit' }}
                  >
                    Learn more
                  </a>
                </Text>
              </div>
            </div>
          </Flex>
          <Flex gap={8} direction="column" align="start" style={{ width: '100%' }}>
            <div style={{ width: 'inherit' }}>
              <Box rounded="small" backgroundColor="greyBackgroundColor" borderColor="uiBorderColor" border>
                {/* Disable board */}
                <div style={{ margin: '16px' }}>
                  <Flex gap={12} direction="row" style={{ width: '100%' }}>
                    <Box>
                      <Toggle
                        areLabelsHidden
                        className="Toggle"
                        size="small"
                        isSelected={!!fieldGroup.disable}
                        onChange={(value: boolean) => {
                          onChange({ ...fieldGroup, disable: value });
                        }}
                      />
                    </Box>
                    <Text type="text2" weight="medium">
                      Disable board
                    </Text>
                  </Flex>
                  <Text type="text3" color="secondary" ellipsis={false} style={{ marginTop: '8px' }}>
                    Skip this board during export to improve performance or to debug potential issues.
                  </Text>
                </div>

                <Divider withoutMargin />

                {/* Disable mirror columns */}
                <div style={{ margin: '16px' }}>
                  <Flex gap={12} direction="row" style={{ width: '100%' }}>
                    <Box>
                      <Toggle
                        areLabelsHidden
                        className="Toggle"
                        size="small"
                        isSelected={!!fieldGroup.disable_mirror_columns}
                        onChange={(value: boolean) => {
                          onChange({ ...fieldGroup, disable_mirror_columns: value });
                        }}
                      />
                    </Box>
                    <Text type="text2" weight="medium">
                      Disable mirror columns
                    </Text>
                  </Flex>
                  <Text type="text3" color="secondary" ellipsis={false} style={{ marginTop: '8px' }}>
                    Skip mirror columns during export to improve performance or to debug potential issues.
                  </Text>
                </div>

                <Divider withoutMargin />

                {/* Diagnosis */}
                <div style={{ margin: '16px' }}>
                  <Text type="text3" color="secondary" ellipsis={false} style={{ marginBottom: '12px' }}>
                    Troubleshoot issues with this board by running a diagnosis and downloading a report.{' '}
                    <a
                      href="https://getgorilla.app/products/spreadsheet-gorilla/troubleshooting"
                      target="_blank"
                      rel="noreferrer"
                      style={{ fontSize: 'inherit', lineHeight: 'inherit' }}
                    >
                      Learn more
                    </a>
                  </Text>
                  <Button
                    kind="secondary"
                    size="small"
                    onClick={() => {
                      setShowBoardDiagnoseModal(true);
                    }}
                    style={{
                      width: '100%',
                    }}
                  >
                    Run diagnose
                  </Button>
                </div>
              </Box>
            </div>
          </Flex>
        </Flex>
      </AppModalContent>
    </AppModal>
  );
}
